import { useEffect, useMemo, useState } from 'react';
import { Layer, Source, useMap } from 'react-map-gl/mapbox';
import classNames from 'classnames';
import throttle from 'lodash/throttle';

import {
  AlertIcon,
  Anchor,
  Dialog,
  Flex,
  GreenCheckIcon,
  Input,
  SearchIcon,
} from '@hcs/design-system';
import { useTrackEngagementEvent } from '@hcs/engagement-tracking';
import { useToastSlice } from '@hcs/toast';
import { MapStyles } from '@hcs/types';

import { VECTILES_TILES_URLS } from '../../constants';
import { useHcMap } from '../../hooks';

import styles from './MlsCoverageLayer.module.css';

interface Props {
  mapId: string;
  beneathLayerId?: string;
}

const DEFAULT_MIN_ZOOM = 4;
const MAX_ZOOM = 22;
const VECTILES_LAYER = 'mls_coverage';
const FILL_LAYER_ID = 'mls-coverage-fill-layer';

export const MlsCoverageLayer = (props: Props) => {
  const {
    actions: { toastOpen },
  } = useToastSlice();
  const { mutate: trackEngagementEvent } = useTrackEngagementEvent();
  const { current: map } = useMap();
  const [isMounted, setIsMounted] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [{ hasNoCoverage, hasPartialCoverage, countyIds }, setCoverageData] =
    useState<{
      countyIds: number[];
      hasPartialCoverage: boolean;
      hasNoCoverage: boolean;
      hasCoverage: boolean;
    }>({
      hasPartialCoverage: false,
      hasNoCoverage: false,
      hasCoverage: false,
      countyIds: [],
    });
  const [notifyOpen, setNotifyOpen] = useState(false);

  const checkForLimitedData = useMemo(
    () =>
      throttle(
        () => {
          /* Ensure that the map and map's style are ready, that the layer has been added to the map,
           * and that all tiles are loaded (else we're wasting effort on checking for mls coverage at this time)  */
          if (!map || !map.loaded || !map.getLayer(FILL_LAYER_ID)) {
            return;
          }
          let hasPartialCoverage = false;
          let hasNoCoverage = false;
          let hasCoverage = false;
          const countyIds =
            map
              .queryRenderedFeatures({ layers: [FILL_LAYER_ID] })
              .map((feature) => {
                if (feature?.properties?.mls_coverage) {
                  if (feature.properties.mls_coverage === 10) {
                    hasNoCoverage = true;
                  } else if (feature.properties.mls_coverage === 20) {
                    hasPartialCoverage = true;
                  } else if (feature.properties.mls_coverage === 30) {
                    hasCoverage = true;
                  }
                }
                return feature && feature.properties?.county_id;
              }) || [];
          setCoverageData({
            hasPartialCoverage,
            hasNoCoverage,
            hasCoverage,
            countyIds: [...new Set(countyIds)],
          });
        },
        300,
        { trailing: true },
      ),
    [map],
  );

  const {
    mapState,
    actions: { hcMapMlsCoverageChange },
  } = useHcMap(props.mapId);
  const layerOpacity = mapState?.mapStyle === MapStyles.Satellite ? 0.45 : 0.15;
  const currentZoom = mapState?.viewport.zoom;
  const { highlightCovered, showLegend, minZoom } = mapState?.mlsCoverage || {};
  const effectiveMinZoom = minZoom ?? DEFAULT_MIN_ZOOM;
  const inLayerZoomRange =
    currentZoom && currentZoom >= effectiveMinZoom && currentZoom <= MAX_ZOOM;
  const showLimitedDataWarning =
    showLegend === 'lg' ||
    (showLegend === 'sm' && (hasNoCoverage || hasPartialCoverage));

  // > 8.5 zoom = show limited/ full /partial  message
  const limitedDataWarning = showLimitedDataWarning ? (
    <Flex
      className={classNames(styles.MlsCoverageLegend, {
        [styles.small]: showLegend === 'sm',
      })}
      direction="column"
      fullWidth
      alignItems="flex-start"
    >
      {showLegend === 'lg' && (
        <div className={styles.LegendHeader}>MLS Data Coverage</div>
      )}
      <Flex gap="8px" alignItems="flex-start" className={styles.MainText}>
        {inLayerZoomRange ? (
          !countyIds.length ? (
            <>Please move the map over the United States to view MLS coverage</>
          ) : currentZoom < 8.5 ? (
            highlightCovered ? (
              'We have MLS data coverage in the shaded areas.'
            ) : (
              'The shaded areas indicate limited MLS coverage.'
            )
          ) : hasNoCoverage || hasPartialCoverage ? (
            <>
              <AlertIcon className={styles.AlertIcon} height={15} />
              <div>
                <div>
                  We have limited data in the{' '}
                  {highlightCovered ? 'unshaded' : 'shaded'} area.
                </div>
                <Anchor
                  dataHcName="mls-coverage-notify"
                  className={styles.NotifyButton}
                  onClick={() => setNotifyOpen(true)}
                >
                  Get notified when more data is available.
                </Anchor>
              </div>
            </>
          ) : (
            <>
              <GreenCheckIcon height={18} /> We have full data coverage in this
              area.
            </>
          )
        ) : (
          <>
            <SearchIcon height={18} /> Please zoom in to see MLS coverage
          </>
        )}
      </Flex>
      <Flex>
        {highlightCovered && (
          <Flex gap="5px">
            <div className={classNames(styles.Icon, styles.full)}></div>
            Full MLS Coverage
          </Flex>
        )}
        <Flex gap="5px">
          <div className={classNames(styles.Icon, styles.partial)}></div>
          Partial MLS Coverage
        </Flex>
        {!highlightCovered && (
          <Flex gap="5px">
            <div className={classNames(styles.Icon, styles.nocoverage)}></div>
            No MLS Coverage
          </Flex>
        )}
      </Flex>
      {showLegend === 'lg' && (
        <div className={styles.LegendInstructions}>
          View HouseCanary MLS data coverage by zooming in on an area of
          interest.
        </div>
      )}
    </Flex>
  ) : null;

  // Load all images needed for monochrome a11y map layer into map
  useEffect(() => {
    if (!isMounted && map) {
      map.on('data', checkForLimitedData);
      map.on('moveend', checkForLimitedData);
      setIsMounted(true);
    }
  }, [map, isMounted, checkForLimitedData]);

  // Cleanup throttled function on unmount
  useEffect(() => {
    return () => {
      checkForLimitedData.cancel();
    };
  }, [checkForLimitedData]);

  // Update mls coverage warning visibility in redux state - this causes the `warningVisible` state value to change,
  // which is read by parent components to potentially change the placement of map controls
  useEffect(() => {
    if (!map) {
      return;
    }
    hcMapMlsCoverageChange({
      mapId: props.mapId,
      mlsCoverage: {
        visible: mapState?.mlsCoverage?.visible ?? false,
        warningVisible: showLimitedDataWarning,
        showLegend: showLegend || false,
      },
    });
  }, [map, showLimitedDataWarning, showLegend]);

  if (!inLayerZoomRange) return limitedDataWarning;

  return (
    <>
      {limitedDataWarning}
      <Source
        key={`${VECTILES_LAYER}-source-key`}
        id={`${VECTILES_LAYER}_source`}
        type="vector"
        tiles={[`${VECTILES_TILES_URLS}/${VECTILES_LAYER}/{z}/{x}/{y}.mvt`]}
        minzoom={effectiveMinZoom}
        maxzoom={MAX_ZOOM}
      >
        <Layer
          key={VECTILES_LAYER}
          id={FILL_LAYER_ID}
          type="fill"
          source={`${VECTILES_LAYER}_source`}
          source-layer={VECTILES_LAYER}
          paint={{
            'fill-color': [
              'step',
              /* -Infinity will be assigned to any `null` value, causing the fallback step def to be used */
              ['number', ['get', 'mls_coverage'], +Infinity],
              `rgba(0,0,0,${highlightCovered ? 0 : layerOpacity})`,
              20,
              `rgba(10, 98, 255, ${layerOpacity})`,
              30,
              `rgba(112, 189, 159,${highlightCovered ? 0.4 : 0})`,
            ],
          }}
          minzoom={effectiveMinZoom}
          maxzoom={MAX_ZOOM}
        />
      </Source>
      <Dialog
        dataHcName="mls-coverage-notify-dialog"
        dataHcEventSection="mls_coverage_notify_dialog"
        active={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        title="Get notified when more data is available in this area"
        actions={[
          {
            dataHcName: 'mls-coverage-notify-submit',
            label: 'Notify Me',
            disabled: !notifyEmail,
            onClick: () => {
              setNotifyOpen(false);
              toastOpen({
                type: 'success',
                title:
                  'Thank you, we will notify you when more data is available.',
              });
              trackEngagementEvent({
                event_name: 'mls-coverage-notify',
                email: notifyEmail,
                event_data: {
                  county_ids: countyIds,
                },
              });
            },
          },
        ]}
      >
        <Input
          dataHcName="mls-coverage-notify-email"
          placeholder="Enter your email address"
          value={notifyEmail}
          onChange={setNotifyEmail}
        />
      </Dialog>
    </>
  );
};
